import React from 'react';
import { Link } from 'gatsby';
import Img from 'gatsby-image';
import { getEventDateRange } from './../util';
import Button from './common/Button';
import { Event } from './Event';
import { CalendarIcon } from '@heroicons/react/outline';

interface FeaturedEventsProps {
  primaryEvent: Event | null;
  secondaryEvents?: Event[];
}

const FeaturedEvents: React.FC<FeaturedEventsProps> = props => {
  const primaryEvent = props.primaryEvent;
  const secondaryEvents = props.secondaryEvents || [];

  return (
    <section className="py-20 mt-20 magazine-bg">
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          {/* Section Title */}
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6 text-balance sans-serif">
              Featured Events
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-primary to-primary/40 mx-auto mb-8 rounded-full"></div>
          </div>

          {/* Primary Featured Event */}
          {primaryEvent && (
            <div className="magazine-card bg-white creative-shadow overflow-hidden mb-8">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                {/* Image Section */}
                <div className="relative h-full min-h-[400px] overflow-hidden">
                  {primaryEvent.image && primaryEvent.image.fluid ? (
                    <Img
                      fluid={primaryEvent.image.fluid}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-primary/10 flex items-center justify-center">
                      <CalendarIcon className="w-24 h-24 text-primary/30" />
                    </div>
                  )}
                </div>

                {/* Content Section */}
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <h2 className="text-2xl lg:text-3xl font-bold text-primary mb-4 leading-tight">
                    {primaryEvent.title || primaryEvent.name}
                  </h2>
                  <div className="flex items-center gap-3 text-foreground/70 mb-8">
                    <CalendarIcon className="w-5 h-5 text-primary" />
                    <span className="font-semibold">
                      {getEventDateRange(
                        new Date(primaryEvent.startDate),
                        new Date(primaryEvent.endDate)
                      )}
                    </span>
                  </div>
                  <Link to={`/events/${primaryEvent.slug}`}>
                    <Button
                      size="lg"
                      className="w-full bg-primary text-white hover:bg-primary/90"
                      text="Learn More"
                    />
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Secondary Events - Half and Half Columns */}
          {secondaryEvents.length > 0 && (
            <div className="grid lg:grid-cols-2 gap-6">
              {secondaryEvents.slice(0, 2).map((event, index) => (
                <div
                  key={event.id || index}
                  className="magazine-card bg-white creative-shadow overflow-hidden flex flex-col"
                >
                  {/* Image - fixed height keeps side-by-side images even (gatsby-image fluid uses intrinsic ratio) */}
                  <div className="relative h-96 overflow-hidden flex-shrink-0">
                    {event.image && event.image.fluid ? (
                      <Img
                        fluid={event.image.fluid}
                        style={{ height: '100%', width: '100%' }}
                        imgStyle={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <div className="w-full h-48 bg-primary/10 flex items-center justify-center">
                        <CalendarIcon className="w-16 h-16 text-primary/30" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="text-xl lg:text-2xl font-bold text-primary mb-3 leading-tight">
                      {event.title || event.name}
                    </h3>
                    <div className="flex items-center gap-2 text-foreground/70 mb-6">
                      <CalendarIcon className="w-4 h-4 text-primary" />
                      <span className="text-sm font-semibold">
                        {getEventDateRange(
                          new Date(event.startDate),
                          new Date(event.endDate)
                        )}
                      </span>
                    </div>
                    <div className="mt-auto">
                      <Link to={`/events/${event.slug}`}>
                        <Button
                          size="md"
                          className="w-full bg-primary text-white hover:bg-primary/90"
                          text="Learn More"
                        />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeaturedEvents;
